import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import Select, { StylesConfig } from 'react-select';
import { FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import ConfirmCommissionActionDialog from '../dialogs/ConfirmCommissionActionDialog';
import CommissionSummary from 'src/models/personnel/CommissionSummary';
import CommissionDetailRow from 'src/models/personnel/CommissionDetailRow';
import { ITableConfig } from 'src/components/common/table';
import { ODataFilter, ODataOptions, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';

interface IPeriodOptionType {
    value: number;
    label: string;
}

interface IDirectorStatus {
    isDirector: boolean;
    tsdNo: number;
    mgrType: string;
    name: string;
}

const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (value: Date | string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
};

// Same Comm_Days highlight rule as CommissionPage/PmLandmenDialog (GET_TSD_LANDMAN [oid 134]).
const commDaysColor = (row: CommissionDetailRow) => (
    (row.commDays ?? 0) > 0 && (row.marginRate ?? 0) > 0 ? '#8ee88e' : '#f5a3a3'
);

const compactSelectStyles: StylesConfig<IPeriodOptionType, false> = {
    control: (provided) => ({ ...provided, minHeight: '3rem', height: '3rem' }),
    valueContainer: (provided) => ({ ...provided, height: '3rem', padding: '0 8px' }),
    input: (provided) => ({ ...provided, margin: '0', padding: '0' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '3rem' }),
    menu: (provided) => ({ ...provided, zIndex: 999 }),
};

// Mirrors RENEWABLE_MANAGER_COMMISSION.fmt's own master block (period_no/process_date/days/
// margin_amt/no_projects/no_landmen/approve_date/approve_by, one row per period, newest first,
// Approve/Rebuild per row) - unlike the main Commission page's single-period search, this loads
// every period for the one logged-in director at once, seeded off the Beginning/Ending Period
// range (defaulted to just the current period, matching the page's own single-period opening view,
// but widenable to pull up history the way Oracle's version always shows it). There's no manager
// concept at all here - it's seeded from whoever is logged in (User.LMSNumber -> tsd_no) matched
// against ANY active row in pm_profit_share_directors (any mgr_type - 'R'=Renewable, 'L'=West Land,
// etc.), not searched for. The route itself stays open to anyone with Back Office access
// (CommissionService.GetDirectorStatus is a soft "what to render" check, not either .fmt's own
// SET_ACCESS Exit_Form gate) - a non-director just sees "Access Denied". The grids/layout render
// the same way for any director type; Populate/Rebuild work for any type too (see
// CommissionService.BuildDirectorCommission's own rollup fallback), only Report stays gated to
// 'R'/'L' (hasReportSupport) since only those have a real SSRS report deployed.
const DirectorCommissionPage = () => {
    const dispatch = useDispatch();
    const hideDialog = () => dispatch(actions.layout.hideDialog());

    const { get: getStatus } = useFetch();
    const [status, setStatus] = useState<IDirectorStatus | undefined>(undefined);
    const [statusLoading, setStatusLoading] = useState<boolean>(true);
    // Populate/Rebuild work for any director type - 'R'/'L' via their own dedicated build procs,
    // any other type via PM_COMM.usp_BuildRollupCommission's org-tree rollup (see
    // CommissionDataAccess.BuildDirectorCommission). Report has no such fallback - only 'R'/'L' have
    // a real SSRS report deployed (see ReportService.GetDirectorMarginsReportFile) - so it stays
    // gated to just those two.
    const hasReportSupport = status?.mgrType === 'R' || status?.mgrType === 'L';

    const { get: getPayPeriods } = useFetch();
    const [periodOptions, setPeriodOptions] = useState<Array<IPeriodOptionType>>([]);
    const [begPeriodOption, setBegPeriodOption] = useState<IPeriodOptionType | null>(null);
    const [endPeriodOption, setEndPeriodOption] = useState<IPeriodOptionType | null>(null);
    const [isSearchingBegPeriods, setIsSearchingBegPeriods] = useState<boolean>(false);
    const [isSearchingEndPeriods, setIsSearchingEndPeriods] = useState<boolean>(false);
    const hasDefaultedPeriods = useRef<boolean>(false);

    const { get: getSummary, response: summaryResponse } = useFetch();
    const { get: getDetail, response: detailResponse } = useFetch();
    const { put: putBuild, response: buildResponse } = useFetch();
    const { put: putApprove, response: approveResponse } = useFetch();
    const { get: getReport, response: reportResponse } = useFetch();
    const [isReporting, setIsReporting] = useState<boolean>(false);
    const [summary, setSummary] = useState<Array<CommissionSummary>>([]);
    const [detail, setDetail] = useState<Array<CommissionDetailRow>>([]);
    const [selectedCommId, setSelectedCommId] = useState<number | undefined>(undefined);
    const [selectedDetailIndex, setSelectedDetailIndex] = useState<number | undefined>(undefined);
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [isBuilding, setIsBuilding] = useState<boolean>(false);
    const [hasQueried, setHasQueried] = useState<boolean>(false);
    const [toast, setToast] = useState<{ message: string; color?: string } | null>(null);
    const [pendingAction, setPendingAction] = useState<
        | { type: 'approve'; row: CommissionSummary }
        | { type: 'rebuild'; row: CommissionSummary }
        | undefined
    >(undefined);

    const insertDash = (periodNumber: number) => {
        const str = periodNumber.toString();
        const insertIndex = str.length - 2;
        return str.slice(0, insertIndex) + '-' + str.slice(insertIndex);
    };

    const normalizePeriodInput = (str: string) => str.replace(/-/g, '');

    const loadPeriodOptions = async (searchTerm: string) => {
        const tableConfig: ITableConfig = { rowCount: 100, idField: 'Id', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.clearFilters();
        options.addPaging(1, 50);
        options.addOrderBy('Id', ODataOrderByDirection.Descending);

        const numericTerm = Number(normalizePeriodInput(searchTerm || ''));
        if (searchTerm && !isNaN(numericTerm)) {
            options.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, numericTerm));
        } else {
            const now = new Date();
            const begDate = new Date(new Date().setMonth(now.getMonth() - 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(new Date().setMonth(now.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            options.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThan, endDate));
            options.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThan, begDate));
        }

        const result = await getPayPeriods(`/v1.0/AccountsPayable/payperiods${options.toString()}`);
        if (Array.isArray(result?.items)) {
            const mapped = result.items.map((pp: { periodNumber: number }) => ({
                value: pp.periodNumber,
                label: insertDash(pp.periodNumber),
            }));
            setPeriodOptions(mapped);
            return mapped;
        }
        return [];
    };

    const begPeriodSearch = _.debounce((inputText: string) => loadPeriodOptions(inputText), 300);
    const endPeriodSearch = _.debounce((inputText: string) => loadPeriodOptions(inputText), 300);

    // Finds the one real pay period whose date range actually contains today - periodOptions[0]
    // (the default "recent periods" window, ordered by Id descending) isn't reliably "today's"
    // period, since Id order doesn't guarantee date order and the window extends 6 months into the
    // future. Falls back to periodOptions[0] if nothing matches (e.g. a gap in the pay calendar).
    const loadCurrentPeriod = async (): Promise<IPeriodOptionType | null> => {
        const tableConfig: ITableConfig = { rowCount: 1, idField: 'Id', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.clearFilters();
        options.addPaging(1, 1);
        options.addOrderBy('Id', ODataOrderByDirection.Descending);

        const today = new Date().toLocaleDateString().replace('/', '-').replace('/', '-');
        options.addFilter(new ODataFilter('beg_date', ODataFilterOperators.LessThanOrEqual, today));
        options.addFilter(new ODataFilter('end_date', ODataFilterOperators.GreaterThanOrEqual, today));

        const result = await getPayPeriods(`/v1.0/AccountsPayable/payperiods${options.toString()}`);
        const match = Array.isArray(result?.items) && result.items[0];
        return match ? { value: match.periodNumber, label: insertDash(match.periodNumber) } : null;
    };

    const runQuery = async (begPeriod: number, endPeriod: number) => {
        if (!status?.tsdNo) {
            return;
        }
        setSummaryLoading(true);
        setToast(null);
        setSelectedCommId(undefined);
        setSelectedDetailIndex(undefined);
        setDetail([]);

        // mgrType (not a literal commission_type) - the service resolves the real filter shape,
        // falling back to standard 'S'/'B' rows + VP-scoped subordinates for any type that isn't a
        // real single-purpose commission type of its own (see CommissionService's own rule).
        const params = new URLSearchParams({
            begPeriod: begPeriod.toString(),
            endPeriod: endPeriod.toString(),
            mgrType: status.mgrType,
            mgrNo: status.tsdNo.toString(),
        });
        const result = await getSummary(`/v1.0/Commission/director-summary?${params.toString()}`);
        setSummary(summaryResponse.ok && Array.isArray(result) ? result : []);
        setHasQueried(true);
        setSummaryLoading(false);
    };

    useEffect(() => {
        (async () => {
            const result = await getStatus('/v1.0/Commission/director-status');
            setStatus(result);
            setStatusLoading(false);
        })();
        loadPeriodOptions('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Defaults both ends of the range to the most recent period once both the director's own
    // status and the initial period options have loaded - opens looking like a single-period view
    // (matching the current period), widenable from there, rather than Oracle's always-everything
    // list.
    useEffect(() => {
        if (hasDefaultedPeriods.current || statusLoading || periodOptions.length === 0 || !status?.tsdNo) {
            return;
        }
        hasDefaultedPeriods.current = true;
        (async () => {
            const current = (await loadCurrentPeriod()) ?? periodOptions[0];
            setBegPeriodOption(current);
            setEndPeriodOption(current);
            runQuery(current.value, current.value);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusLoading, periodOptions, status]);

    const executeSearch = () => {
        if (!begPeriodOption?.value || !endPeriodOption?.value) {
            setToast({ message: 'A beginning and ending pay period must be entered.', color: 'rgba(255,0,0,0.5)' });
            return;
        }
        runQuery(begPeriodOption.value, endPeriodOption.value);
    };

    const selectRow = async (row: CommissionSummary) => {
        setSelectedCommId(row.commId);
        setSelectedDetailIndex(undefined);
        setDetailLoading(true);
        const result = await getDetail(`/v1.0/Commission/${row.commId}/detail`);
        setDetail(detailResponse.ok && Array.isArray(result) ? result : []);
        setDetailLoading(false);
    };

    // Mirrors ADD_PERIOD/B_BUILD - period-scoped, targets the Ending Period field (Oracle's own
    // "Add Period" - set the end of the range to a not-yet-populated period and this adds it to the
    // list). Labeled "Populate" rather than "Build"/"Add Period" - what it actually does is populate
    // commission rows from the underlying time sheet/staffing data (or, for a VP/Area Manager tier
    // director, roll up whatever real commission already exists under their downstream org tree -
    // see CommissionDataAccess.BuildDirectorCommission), not construct a new period record.
    const doPopulate = async () => {
        if (!endPeriodOption?.value || !status?.tsdNo) {
            setToast({ message: 'An ending pay period must be entered.', color: 'rgba(255,0,0,0.5)' });
            return;
        }
        setIsBuilding(true);
        setToast(null);
        const params = new URLSearchParams({ mgrType: status.mgrType, periodNo: endPeriodOption.value.toString(), mgrNo: status.tsdNo.toString() });
        await putBuild(`/v1.0/Commission/build-director?${params.toString()}`);
        setIsBuilding(false);

        if (!buildResponse.ok) {
            setToast({ message: 'Unable to populate. Please try again.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        setToast({ message: `Period ${endPeriodOption.label}: commission data populated.` });
        await runQuery(begPeriodOption?.value ?? endPeriodOption.value, endPeriodOption.value);
    };

    // Mirrors each director type's own "Margin Report"/"Excel Report" button -
    // RENEWABLE_MANAGER_COMMISSION.fmt's B_REPORT -> XL_RN_Manager_Margins and
    // WEST_LAND_MGR_COMMISSION.fmt's B_EXCEL -> XL_PM_Margin call different legacy procedures with
    // different parameter shapes, but the SSRS reports actually deployed for both were normalized
    // to the same single-period shape (see ReportService.GetDirectorMarginsReportFile) - one
    // GetReport/DirectorMargins endpoint, dispatched by mgrType, covers both. Reads whichever
    // period is currently selected in the summary grid (Oracle's own :excel.period_no comes from
    // the current record there), falling back to the Ending Period field if no row is selected yet.
    const doReport = async () => {
        const periodNo = summary.find((row) => row.commId === selectedCommId)?.periodNo ?? endPeriodOption?.value;
        if (!periodNo) {
            setToast({ message: 'Please select a period first.', color: 'rgba(255,0,0,0.5)' });
            return;
        }
        if (!hasReportSupport) {
            setToast({ message: 'Reporting is not yet available for this director type.', color: 'rgba(255,0,0,0.5)' });
            return;
        }
        setIsReporting(true);
        setToast(null);
        const params = new URLSearchParams({ mgrType: status?.mgrType ?? '', periodNo: periodNo.toString() });
        await getReport(`/v1.0/reports/GetReport/DirectorMargins?${params.toString()}`);
        setIsReporting(false);

        if (!reportResponse.ok) {
            setToast({ message: 'Unable to generate the report. Please try again.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        if (reportResponse.status === 204) {
            setToast({ message: 'No commission records matched that period.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'DirectorMargins.xlsx';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 116] - stamps approve_date/approve_by on this row.
    const doApprove = async (row: CommissionSummary) => {
        setToast(null);
        const result = await putApprove(`/v1.0/Commission/${row.commId}/approve`);
        if (!approveResponse.ok) {
            setToast({ message: 'Unable to approve. Please try again.', color: 'rgba(255,0,0,0.5)' });
            return;
        }
        setSummary((prev) => prev.map((r) => (
            r.commId === row.commId ? { ...r, approveDate: new Date(), approveBy: result?.approveBy } : r
        )));
        setToast({ message: `${row.periodNo}'s commission has been approved.` });
    };

    // Rebuild is really "populate this period again" via the same mechanism as the page-level
    // Populate above.
    const doRebuildRow = async (row: CommissionSummary) => {
        setToast(null);
        if (!status?.tsdNo) {
            return;
        }
        const params = new URLSearchParams({ mgrType: status.mgrType, periodNo: row.periodNo.toString(), mgrNo: status.tsdNo.toString() });
        await putBuild(`/v1.0/Commission/build-director?${params.toString()}`);

        if (!buildResponse.ok) {
            setToast({ message: 'Unable to rebuild. Please try again.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        if (begPeriodOption?.value && endPeriodOption?.value) {
            await runQuery(begPeriodOption.value, endPeriodOption.value);
        }
        setToast({ message: `${row.periodNo}: commission data populated.` });
    };

    const approveRow = (row: CommissionSummary) => {
        setPendingAction({ type: 'approve', row });
        dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
    };

    // Mirrors the main Commission page's own rebuildRow - only confirms when the row is already
    // approved (rebuilding an approved period is an implicit un-approve); otherwise rebuilds
    // immediately.
    const rebuildRow = (row: CommissionSummary) => {
        if (row.approveDate) {
            setPendingAction({ type: 'rebuild', row });
            dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
            return;
        }
        doRebuildRow(row);
    };

    const confirmPendingAction = async () => {
        if (pendingAction?.type === 'approve') {
            hideDialog();
            doApprove(pendingAction.row);
            setPendingAction(undefined);
        } else if (pendingAction?.type === 'rebuild') {
            hideDialog();
            doRebuildRow(pendingAction.row);
            setPendingAction(undefined);
        }
    };

    if (statusLoading) {
        return (
            <div>
                <PersonnelNavBar />
                <div className="bold-centered" style={{ padding: '2rem' }}><LoadingSpinner /></div>
            </div>
        );
    }

    if (!status?.isDirector) {
        return (
            <div>
                <PersonnelNavBar />
                <div className="bold-centered" style={{ padding: '2rem' }}>
                    Access Denied
                </div>
            </div>
        );
    }

    return (
        <div>
            <PersonnelNavBar />
            {toast && (
                <PersonnelToast message={toast.message} color={toast.color} onClose={() => setToast(null)} />
            )}
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <div style={{ width: 'fit-content', margin: '0 auto' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                    <div style={{ width: '14rem', flexShrink: 0 }}>
                                        <Fieldset label="Beginning Period" style={{ border: 0, padding: 0 }}>
                                            <Select
                                                name="begPeriodNo"
                                                placeholder="Period#"
                                                options={periodOptions}
                                                value={begPeriodOption}
                                                onChange={(option: IPeriodOptionType | null) => setBegPeriodOption(option)}
                                                onInputChange={(inputText: string) => begPeriodSearch(inputText)}
                                                filterOption={(option, inputValue) => (
                                                    normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                                                )}
                                                onMenuOpen={() => setIsSearchingBegPeriods(true)}
                                                onMenuClose={() => setIsSearchingBegPeriods(false)}
                                                onBlur={() => setIsSearchingBegPeriods(false)}
                                                menuIsOpen={isSearchingBegPeriods}
                                                isClearable={true}
                                                isSearchable={true}
                                                styles={compactSelectStyles}
                                            />
                                        </Fieldset>
                                    </div>
                                    <div style={{ width: '14.5rem', paddingLeft: '.5rem', flexShrink: 0 }}>
                                        <Fieldset label="Ending Period" style={{ border: 0, padding: 0 }}>
                                            <Select
                                                name="endPeriodNo"
                                                placeholder="Period#"
                                                options={periodOptions}
                                                value={endPeriodOption}
                                                onChange={(option: IPeriodOptionType | null) => setEndPeriodOption(option)}
                                                onInputChange={(inputText: string) => endPeriodSearch(inputText)}
                                                filterOption={(option, inputValue) => (
                                                    normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                                                )}
                                                onMenuOpen={() => setIsSearchingEndPeriods(true)}
                                                onMenuClose={() => setIsSearchingEndPeriods(false)}
                                                onBlur={() => setIsSearchingEndPeriods(false)}
                                                menuIsOpen={isSearchingEndPeriods}
                                                isClearable={true}
                                                isSearchable={true}
                                                styles={compactSelectStyles}
                                            />
                                        </Fieldset>
                                    </div>
                                    <div style={{ width: '20rem', paddingLeft: '.5rem', flexShrink: 0 }}>
                                        <Fieldset label={`Director (${status.mgrType})`} style={{ border: 0, padding: 0 }}>
                                            <div style={{ height: '3rem', display: 'flex', alignItems: 'center', padding: '0 .5rem' }}>
                                                ({status.tsdNo}) {status.name}
                                            </div>
                                        </Fieldset>
                                    </div>
                                    <div style={{ display: 'flex', flexShrink: 0, paddingLeft: '1rem', gap: '1rem' }}>
                                        <Button
                                            text="Search"
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', borderRadius: '5px' }}
                                            onClick={() => executeSearch()}
                                        />
                                        <Button
                                            title="Populate Commission Data"
                                            text={isBuilding ? 'Populating...' : 'Populate'}
                                            loading={isBuilding}
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', width: '9rem', height: '3rem', borderRadius: '5px' }}
                                            onClick={() => doPopulate()}
                                        />
                                        <Button
                                            text={isReporting ? 'Working...' : 'Report'}
                                            loading={isReporting}
                                            className="blue-hover-button"
                                            style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', borderRadius: '5px' }}
                                            onClick={() => doReport()}
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </Panel>
                    </div>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Commission">
                            <div style={{ overflowY: 'auto', overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderSpacing: 0, borderCollapse: 'separate' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>Period</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Days</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Margin Amount</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Projects</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Landmen</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Approve Date</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Approve By</th>
                                            <th style={{ whiteSpace: 'nowrap' }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summaryLoading && (
                                            <tr><td colSpan={8} className="bold-centered"><LoadingSpinner /></td></tr>
                                        )}
                                        {!summaryLoading && summary.map((row) => {
                                            // arHistoryCell sets its own opaque background-color, which paints over (masks)
                                            // any backgroundColor set on the parent <tr> - the selection highlight has to go
                                            // on each <td> instead.
                                            const rowBg = row.commId === selectedCommId ? '#fff2a8' : '#fff';
                                            return (
                                            <tr
                                                key={row.commId}
                                                onClick={() => selectRow(row)}
                                                style={{ textAlign: 'center', cursor: 'pointer' }}
                                            >
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.periodNo}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.commDays}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, textAlign: 'right' }}>{formatCurrency(row.marginAmt)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.noProspects}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.noLandman}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{formatDate(row.approveDate)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.approveBy}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, whiteSpace: 'nowrap' }}>
                                                    <Button
                                                        title={row.approveDate ? 'Approved' : 'Approve'}
                                                        text=""
                                                        className="blue-hover-button"
                                                        style={{ border: 'none', background: 'transparent', padding: '.2rem' }}
                                                        disabled={!!row.approveDate}
                                                        reactIcon={FaCheckCircle}
                                                        iconStyle={{ fill: row.approveDate ? '#bbb' : 'green', width: '1.5rem', height: '1.5rem', verticalAlign: 'middle' }}
                                                        onClick={(e) => { e.stopPropagation(); approveRow(row); }}
                                                    />
                                                    <Button
                                                        title="Rebuild"
                                                        text=""
                                                        className="blue-hover-button"
                                                        style={{ border: 'none', background: 'transparent', padding: '.2rem' }}
                                                        reactIcon={FaSyncAlt}
                                                        iconStyle={{ fill: 'rgb(99, 147, 193)', width: '1.5rem', height: '1.5rem', verticalAlign: 'middle' }}
                                                        onClick={(e) => { e.stopPropagation(); rebuildRow(row); }}
                                                    />
                                                </td>
                                            </tr>
                                            );
                                        })}
                                        {!summaryLoading && hasQueried && summary.length === 0 && (
                                            <tr><td colSpan={8} className="bold-centered">No Data Found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Commission Detail">
                            <div style={{ overflowY: 'auto', overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderSpacing: 0, borderCollapse: 'separate' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>Client</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Project</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Subcode</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Employee #</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Landman Name</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Bill Rate</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Day Rate</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Margin Rate</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Days</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Margin Type</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Margin Amount</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Invoice #</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailLoading && (
                                            <tr><td colSpan={12} className="bold-centered"><LoadingSpinner /></td></tr>
                                        )}
                                        {!detailLoading && detail.map((row, index) => {
                                            // Same arHistoryCell-masks-the-<tr> issue as the summary grid above - background
                                            // has to be set per-<td>. The Days cell keeps its own commDaysColor status color
                                            // even when selected, rather than being hidden behind the selection tint.
                                            const rowBg = index === selectedDetailIndex ? '#fff2a8' : '#fff';
                                            return (
                                            <tr
                                                key={`${row.commId}-${row.clNo}-${row.prNo}-${row.tsdNo}-${index}`}
                                                onClick={() => setSelectedDetailIndex(index)}
                                                style={{ textAlign: 'center', cursor: 'pointer' }}
                                            >
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.clNo}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.prNo}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.scNo}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.tsdNo}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.name}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, textAlign: 'right' }}>{formatCurrency(row.billRate)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, textAlign: 'right' }}>{formatCurrency(row.dayRate)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, textAlign: 'right' }}>{formatCurrency(row.marginRate)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: commDaysColor(row) }}>{row.commDays}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.dCommType}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg, textAlign: 'right' }}>{formatCurrency(row.marginAmt)}</td>
                                                <td className="arHistoryCell" style={{ backgroundColor: rowBg }}>{row.invoiceNo}</td>
                                            </tr>
                                            );
                                        })}
                                        {!detailLoading && selectedCommId && detail.length === 0 && (
                                            <tr><td colSpan={12} className="bold-centered">No Data Found</td></tr>
                                        )}
                                        {!detailLoading && !selectedCommId && (
                                            <tr><td colSpan={12} className="bold-centered">Select a period above to view detail</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <ConfirmCommissionActionDialog
                dialogName={DialogList.CONFIRM_COMMISSION_ACTION}
                hideDialog={hideDialog}
                heading={pendingAction?.type === 'approve' ? 'Approve Commission' : 'Rebuild Commission'}
                label={
                    pendingAction?.type === 'approve'
                        ? `Approve commission for period ${pendingAction.row.periodNo}?`
                        : 'The commission for this period has already been approved. Are you sure you want to rebuild it?'
                }
                confirmText={pendingAction?.type === 'approve' ? 'Approve' : 'Rebuild'}
                onConfirm={confirmPendingAction}
            />
        </div>
    );
};

export default DirectorCommissionPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/DirectorCommissionPage.tsx